import React, { useState, useEffect } from 'react';
import * as usersApi from '../api/users';
import client from '../api/client';

const STATUS_LABELS = {
  none:             { label: '未契約',       bg: '#f0f0f0', color: '#999' },
  trialing:         { label: 'トライアル',   bg: '#fff3cd', color: '#856404' },
  active:           { label: '有効',         bg: '#e6f9f0', color: '#06a96e' },
  cancel_scheduled: { label: 'キャンセル予定', bg: '#fff8e1', color: '#f59e0b' },
  past_due:         { label: '支払遅延',     bg: '#fff0ee', color: '#e0324b' },
  canceled:         { label: 'キャンセル済', bg: '#f0f0f0', color: '#777' },
  unpaid:           { label: '未払い',       bg: '#fff0ee', color: '#e0324b' },
};

const PLAN_LABELS = {
  free:     { label: 'フリー',       color: '#888' },
  standard: { label: 'スタンダード', color: '#3b82f6' },
  premium:  { label: 'プレミアム',   color: '#9B5FE3' },
};

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] ?? STATUS_LABELS.none;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, background: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
}

/* ── 詳細パネル ── */
function UserDetailPanel({ user, onClose, onAction }) {
  const info = (label, value) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 13, color: value ? '#222' : '#ccc' }}>{value || '—'}</div>
    </div>
  );

  const plan = PLAN_LABELS[user.plan] ?? PLAN_LABELS.free;

  return (
    <>
      {/* オーバーレイ */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 200,
        }}
      />
      {/* パネル */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 380,
        background: '#fff', zIndex: 201,
        boxShadow: '-4px 0 32px rgba(0,0,0,0.12)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* ヘッダー */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid #eee',
          background: 'linear-gradient(135deg, #f8f5ff, #fff)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#333' }}>
                {user.full_name || <span style={{ color: '#ccc' }}>氏名未登録</span>}
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>{user.email}</div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none', fontSize: 18, cursor: 'pointer',
                color: '#999', lineHeight: 1, padding: '4px 8px',
              }}
            >✕</button>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            <StatusBadge status={user.subscription_status} />
            <span style={{
              display: 'inline-block', padding: '2px 10px', borderRadius: 20,
              fontSize: 11, fontWeight: 700,
              background: '#f3f0ff', color: plan.color,
            }}>
              {plan.label}プラン
            </span>
            <span style={{
              display: 'inline-block', padding: '2px 10px', borderRadius: 20,
              fontSize: 11, fontWeight: 700,
              background: user.is_active ? '#e6f9f0' : '#fff0ee',
              color: user.is_active ? '#06a96e' : '#e0324b',
            }}>
              {user.is_active ? '有効' : '無効'}
            </span>
          </div>
        </div>

        {/* スクロール本体 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* お客様情報 */}
          <Section>👤 お客様情報</Section>
          <Grid>
            {info('氏名', user.full_name)}
            {info('氏名（フリガナ）', user.full_name_kana)}
            {info('電話番号', user.phone)}
            {info('郵便番号', user.postal_code)}
          </Grid>
          <div style={{ marginTop: 12 }}>
            {info('住所', user.address)}
          </div>

          {/* 法人情報 */}
          <Section>🏢 法人情報</Section>
          <Grid>
            {info('法人名', user.company_name)}
            {info('法人名（フリガナ）', user.company_name_kana)}
          </Grid>

          {/* サブスクリプション */}
          <Section>💳 サブスクリプション</Section>
          <Grid>
            {info('ステータス', STATUS_LABELS[user.subscription_status]?.label ?? '—')}
            {info('プラン', plan.label)}
            {info('期限',
              user.subscription_period_end
                ? new Date(user.subscription_period_end).toLocaleDateString('ja-JP')
                : '—'
            )}
            {info('登録日', new Date(user.created_at).toLocaleDateString('ja-JP'))}
          </Grid>

          {/* 未入力警告 */}
          {(!user.full_name || !user.phone || !user.address) && (
            <div style={{
              marginTop: 16, padding: '10px 14px',
              background: '#fffbeb', border: '1px solid #fbd38d',
              borderRadius: 10, fontSize: 12, color: '#744210',
            }}>
              ⚠️ 未入力の必須項目があります
            </div>
          )}
        </div>

        {/* 操作ボタン（adminは除外） */}
        {user.role !== 'admin' && (
          <div style={{
            padding: '16px 24px', borderTop: '1px solid #eee',
            display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0,
          }}>
            {/* サブスクリプション手動変更 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#666', flexShrink: 0 }}>サブスク状態：</span>
              <select
                style={{
                  flex: 1, padding: '6px 8px', fontSize: 12, borderRadius: 8,
                  border: '1px solid #d0c0f0', cursor: 'pointer', background: '#fff',
                }}
                value={user.subscription_status}
                onChange={e => onAction('subscription', user, e.target.value)}
              >
                <option value="none">未契約</option>
                <option value="trialing">トライアル</option>
                <option value="active">有効</option>
                <option value="past_due">支払遅延</option>
                <option value="canceled">キャンセル済</option>
                <option value="unpaid">未払い</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-secondary"
                style={{ flex: 1, padding: '8px', fontSize: 12 }}
                onClick={() => onAction('invite', user)}
              >
                📧 招待メール送信
              </button>
              <button
                className={`btn ${user.is_active ? 'btn-secondary' : 'btn-primary'}`}
                style={{ flex: 1, padding: '8px', fontSize: 12 }}
                onClick={() => onAction('toggle', user)}
              >
                {user.is_active ? '🚫 無効化' : '✅ 有効化'}
              </button>
            </div>
            <button
              className="btn btn-danger"
              style={{ width: '100%', padding: '8px', fontSize: 12 }}
              onClick={() => onAction('delete', user)}
            >
              🗑️ アカウント削除
            </button>
          </div>
        )}
      </div>
    </>
  );
}

const Section = ({ children }) => (
  <div style={{
    fontSize: 11, fontWeight: 700, color: '#9B5FE3',
    borderBottom: '1px solid #e9d8fd',
    paddingBottom: 4, marginTop: 20, marginBottom: 10,
    letterSpacing: '0.05em',
  }}>
    {children}
  </div>
);

const Grid = ({ children }) => (
  <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
    {children}
  </div>
);

/* ── メイン ── */
export default function AdminPage() {
  const [users, setUsers]           = useState([]);
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [loading, setLoading]       = useState(false);
  const [fetching, setFetching]     = useState(true);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [selectedUser, setSelected] = useState(null);

  const fetchUsers = async () => {
    try { setUsers(await usersApi.list()); }
    catch { setError('ユーザー一覧の取得に失敗しました'); }
    finally { setFetching(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const flash = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); }
    else { setSuccess(msg); setError(''); setTimeout(() => setSuccess(''), 3000); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!email || !password) { flash('メールアドレスとパスワードを入力してください', true); return; }
    setLoading(true);
    try {
      await usersApi.create(email, password);
      setEmail(''); setPassword('');
      flash('ユーザーを作成しました');
      fetchUsers();
    } catch (err) {
      flash(err.response?.data?.detail ?? 'ユーザー作成に失敗しました', true);
    } finally { setLoading(false); }
  };

  /* 詳細パネルからのアクション */
  const handleAction = async (type, user, value) => {
    try {
      if (type === 'invite') {
        await client.post(`/users/${user.id}/invite`);
        flash(`${user.email} に招待メールを送信しました`);
      } else if (type === 'toggle') {
        await usersApi.update(user.id, { is_active: !user.is_active });
        flash(`${user.email} を${user.is_active ? '無効化' : '有効化'}しました`);
        setSelected(prev => prev ? { ...prev, is_active: !prev.is_active } : null);
      } else if (type === 'delete') {
        if (!window.confirm(`「${user.email}」を削除しますか？`)) return;
        await usersApi.remove(user.id);
        flash(`${user.email} を削除しました`);
        setSelected(null);
      } else if (type === 'subscription') {
        await client.patch(`/billing/admin/users/${user.id}/subscription`, { status: value });
        flash(`サブスクリプションを「${STATUS_LABELS[value]?.label ?? value}」に変更しました`);
        setSelected(prev => prev ? { ...prev, subscription_status: value } : null);
      }
      fetchUsers();
    } catch (err) {
      flash(err.response?.data?.detail ?? '操作に失敗しました', true);
    }
  };

  const roleBadge = (role) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    background: role === 'admin' ? '#9B5FE3' : '#eee',
    color: role === 'admin' ? '#fff' : '#666',
  });

  return (
    <div className="page">
      <div className="page-header animate-in">
        <h1 className="page-title">ユーザー管理</h1>
        <p className="page-subtitle">アカウントの発行・有効化・削除・課金管理を行います</p>
      </div>

      <div className="page-body two-col" style={{ alignItems: 'flex-start' }}>

        {/* ── 新規作成フォーム ── */}
        <div className="two-col-left animate-in-delay-1">
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
              新規アカウント発行
            </h2>
            <div className="form-group">
              <label className="form-label">メールアドレス</label>
              <input className="form-input" type="email" placeholder="user@example.com"
                value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">初期パスワード</label>
              <input className="form-input" type="password" placeholder="••••••••"
                value={password} onChange={e => setPassword(e.target.value)} />
            </div>
            {error   && <div className="msg msg-error">{error}</div>}
            {success && <div className="msg msg-success">{success}</div>}
            <button type="submit" className="btn btn-primary" disabled={loading}
              style={{ width: '100%', padding: '10px' }}>
              {loading ? <><span className="spinner" />作成中…</> : 'アカウントを発行'}
            </button>
          </form>
        </div>

        {/* ── 縦区切り ── */}
        <div className="two-col-divider" />

        {/* ── ユーザー一覧 ── */}
        <div className="two-col-right animate-in-delay-2">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 15, fontWeight: 600 }}>アカウント一覧</h2>
            <span style={{
              fontSize: 10, color: 'var(--text-muted)', background: 'var(--border-light)',
              padding: '2px 10px', borderRadius: 20, border: '1px solid var(--border)',
            }}>
              {users.length} 件
            </span>
            <span style={{ fontSize: 11, color: '#aaa', marginLeft: 4 }}>
              行をクリックして詳細表示
            </span>
          </div>

          {fetching ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <span className="spinner spinner-dark" style={{ width: 22, height: 22 }} />
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">◉</div>
              <div className="empty-text">ユーザーがいません</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)', color: 'var(--text-muted)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px' }}>氏名 / メール</th>
                    <th style={{ padding: '8px 10px' }}>電話番号</th>
                    <th style={{ padding: '8px 10px' }}>ロール</th>
                    <th style={{ padding: '8px 10px' }}>プラン</th>
                    <th style={{ padding: '8px 10px' }}>サブスク状態</th>
                    <th style={{ padding: '8px 10px' }}>期限</th>
                    <th style={{ padding: '8px 10px' }}>状態</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr
                      key={u.id}
                      onClick={() => setSelected(u)}
                      style={{
                        borderBottom: '1px solid var(--border-light)',
                        cursor: 'pointer',
                        background: selectedUser?.id === u.id ? '#f8f5ff' : 'transparent',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => { if (selectedUser?.id !== u.id) e.currentTarget.style.background = '#fafafa'; }}
                      onMouseLeave={e => { if (selectedUser?.id !== u.id) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {/* 氏名 / メール */}
                      <td style={{ padding: '10px 10px' }}>
                        <div style={{ fontWeight: 700, color: '#333', fontSize: 13 }}>
                          {u.full_name || <span style={{ color: '#ccc', fontWeight: 400 }}>未登録</span>}
                        </div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{u.email}</div>
                      </td>
                      {/* 電話番号 */}
                      <td style={{ padding: '10px 10px', color: '#555', fontSize: 12 }}>
                        {u.phone || <span style={{ color: '#ccc' }}>—</span>}
                      </td>
                      {/* ロール */}
                      <td style={{ padding: '10px 10px' }}>
                        <span style={roleBadge(u.role)}>{u.role}</span>
                      </td>
                      {/* プラン */}
                      <td style={{ padding: '10px 10px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          color: PLAN_LABELS[u.plan]?.color ?? '#888',
                        }}>
                          {PLAN_LABELS[u.plan]?.label ?? u.plan}
                        </span>
                      </td>
                      {/* サブスク状態 */}
                      <td style={{ padding: '10px 10px' }}>
                        {u.role !== 'admin'
                          ? <StatusBadge status={u.subscription_status} />
                          : <span style={{ color: '#ddd' }}>—</span>}
                      </td>
                      {/* 期限 */}
                      <td style={{ padding: '10px 10px', color: '#888', fontSize: 11 }}>
                        {u.subscription_period_end
                          ? new Date(u.subscription_period_end).toLocaleDateString('ja-JP')
                          : '—'}
                      </td>
                      {/* 有効/無効 */}
                      <td style={{ padding: '10px 10px' }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                          fontSize: 11, fontWeight: 700,
                          background: u.is_active ? '#e6f9f0' : '#ffeef0',
                          color: u.is_active ? '#06a96e' : '#e0324b',
                        }}>
                          {u.is_active ? '有効' : '無効'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 詳細パネル */}
      {selectedUser && (
        <UserDetailPanel
          user={selectedUser}
          onClose={() => setSelected(null)}
          onAction={handleAction}
        />
      )}
    </div>
  );
}
